import Vue from 'vue';
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router';
import Arbeitsplaetze from '@/components/Arbeitsplaetze/Arbeitsplaetze.vue';
import Briefarten from '@/components/Briefarten/Briefarten.vue';
import Einstellungen from '@/components/Einstellungen/Einstellungen.vue';
import ElgaUebertagung from '@/views/ElgaUebertagung.vue';
import Terminanmeldungen from '@/views/Terminanmeldungen.vue';
import EmailsLogbuch from '@/components/Logbuch/EmailLogbuch.vue';
import Home from '@/views/Home.vue';
import ImpfDbImpfstoffe from '@/components/Impfdb/ImpfDbImpfstoffe.vue';
import Impfempfehlungen from '@/components/Impfempfehlungen/Impfempfehlungen.vue';
import Impferinnerungen from '@/views/Impferinnerungen.vue';
import Impferinnerungstexte from '@/components/Impferinnerungstexte/Impferinnerungstexte.vue';
import Impfstoffe from '@/components/Impfstoffe/Impfstoffe.vue';
import JawaLogbuch from '@/components/Logbuch/JawaLogbuch.vue';
import Kassafuehrung from '@/views/Kassafuehrung.vue';
import Lagerfuehrung from '@/views/Lagerfuehrung.vue';
import Leistungen from '@/components/Leistungen/Leistungen.vue';
import Logbuch from '@/views/Logbuch.vue';
import Login from '@/views/Login.vue';
import Personen from '@/components/Personen/Personen.vue';
import Reisearten from '@/components/Reisearten/Reisearten.vue';
import Reiseziele from '@/components/Reiseziele/Reiseziele.vue';
import Rezepte from '@/components/Rezepte/Rezepte.vue';
import Anforderungen from '@/components/Anforderungen/Anforderungen.vue';
import Terminarten from '@/components/Terminarten/Terminarten.vue';
import Terminplaetze from '@/components/Terminplaetze/Terminplaetze.vue';
import Terminkalender from '@/views/Terminkalender.vue';
import Texte from '@/components/Texte/Texte.vue';
import Titel from '@/components/Titel/Titel.vue';
import Untersuchungen from '@/components/Untersuchungen/Untersuchungen.vue';
import User from '@/types/User';
import Users from '@/components/Users/Users.vue';
import Versicherungen from '@/components/Versicherungen/Versicherungen.vue';
import Zahlungseingang from '@/views/Zahlungseingang.vue';
import Zahlungserinnerungen from '@/views/Zahlungserinnerungen.vue';
import Dienstorte from '@/components/Dienstorte/Dienstorte.vue';
import Diagnosen from '@/components/Diagnosen/Diagnosen.vue';
import Patientengruppen from '@/components/Patientengruppen/Patientengruppen.vue';
import store from '@/store';
import Impfaktionen from '@/components/Impfaktionen/Impfaktionen.vue';
import Studien from '@/components/Studien/Studien.vue';

const createGuard = (roles: string[]) => (to: Route, from: Route, next: NavigationGuardNext): void => {
    const user = store.getters['authentication/user'] as User | null;

    if (user?.roles && !user.roles.some((role: string) => roles.includes(role))) {
        return next({ path: '/' });
    }

    next();
};

const adminGuard = createGuard(['admin', 'system']);
const adminUserGuard = createGuard(['admin', 'anwender', 'system']);

const routes: RouteConfig[] = [
    {
        path: '/',
        component: Home,
        meta: {
            name: 'Impfverwaltung',
            requiresAuth: true,
        },
        beforeEnter: (to: Route, from: Route, next: NavigationGuardNext): void => {
            const user = store.getters['authentication/user'] as User | null;

            if (user?.roles?.some((role: string) => role === 'lager') && user.roles.length === 1) {
                return next({ path: '/lager' });
            }

            next();
        },
    },
    {
        path: '/login',
        component: Login,
        meta: {
            name: 'Login',
        },
    },
    {
        path: '/arbeitsplaetze',
        component: Arbeitsplaetze,
        meta: {
            name: 'Arbeitsplätze',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/impfaktionen',
        component: Impfaktionen,
        meta: {
            name: 'Impfaktionen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/impfstoffe',
        component: Impfstoffe,
        meta: {
            name: 'Impfstoffe',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/diagnosen',
        component: Diagnosen,
        meta: {
            name: 'Diagnosen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/dienstorte',
        component: Dienstorte,
        meta: {
            name: 'Dienstorte',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/leistungen',
        component: Leistungen,
        meta: {
            name: 'Leistungen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/patientengruppen',
        component: Patientengruppen,
        meta: {
            name: 'Patientengruppen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/personen',
        component: Personen,
        meta: {
            name: 'Personen',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/reisearten',
        component: Reisearten,
        meta: {
            name: 'Reisearten',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/reiseziele',
        component: Reiseziele,
        meta: {
            name: 'Reiseziele',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/studien',
        component: Studien,
        meta: {
            name: 'Studien',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/texte',
        component: Texte,
        meta: {
            name: 'Texte',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/titel',
        component: Titel,
        meta: {
            name: 'Titel',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/kassa',
        component: Kassafuehrung,
        meta: {
            name: 'Kassaabschluss',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/zahlungseingang',
        component: Zahlungseingang,
        meta: {
            name: 'Zahlungseingang',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/zahlungserinnerungen',
        component: Zahlungserinnerungen,
        meta: {
            name: 'Zahlungserinnerungen',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/lager',
        component: Lagerfuehrung,
        meta: {
            name: 'Lagerführung',
            requiresAuth: true,
        },
    },
    {
        path: '/logbuch',
        component: Logbuch,
        meta: {
            name: 'Logbuch',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/jawa/logbuch',
        component: JawaLogbuch,
        meta: {
            name: 'Logs',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/emails',
        component: EmailsLogbuch,
        meta: {
            name: 'Logs',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/elga',
        component: ElgaUebertagung,
        meta: {
            name: 'ELGA Übertragung',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/terminanmeldungen',
        component: Terminanmeldungen,
        meta: {
            name: 'Terminanmeldungen',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/anforderungen',
        component: Anforderungen,
        meta: {
            name: 'Termin Anforderungen',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/impfstoffe-impfdb',
        component: ImpfDbImpfstoffe,
        meta: {
            name: 'ImpfDB Impfstoffe',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/impferinnerungen',
        name: 'Impferinnerungen',
        component: Impferinnerungen,
        meta: {
            name: 'Impferinnerungen',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/terminkalender',
        name: 'Terminkalender',
        component: Terminkalender,
        meta: {
            name: 'Terminkalender',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/impfempfehlungen',
        name: 'Impfempfehlungen',
        component: Impfempfehlungen,
        meta: {
            name: 'Impfempfehlungen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/impferinnerungstexte',
        name: 'Impferinnerungstexte',
        component: Impferinnerungstexte,
        meta: {
            name: 'Impferinnerungstexte',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/rezepte',
        name: 'Rezepte',
        component: Rezepte,
        meta: {
            name: 'Rezepte',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/briefarten',
        name: 'Briefarten',
        component: Briefarten,
        meta: {
            name: 'Briefarten',
            requiresAuth: true,
        },
        beforeEnter: adminUserGuard,
    },
    {
        path: '/versicherungen',
        name: 'Versicherungen',
        component: Versicherungen,
        meta: {
            name: 'Versicherungen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/untersuchungen',
        name: 'Untersuchungen',
        component: Untersuchungen,
        meta: {
            name: 'Untersuchungen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/benutzer',
        name: 'Benutzer',
        component: Users,
        meta: {
            name: 'Benutzer',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/terminarten',
        name: 'Terminarten',
        component: Terminarten,
        meta: {
            name: 'Terminarten',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/terminplaetze',
        name: 'Terminplätze',
        component: Terminplaetze,
        meta: {
            name: 'Terminplätze',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
    {
        path: '/einstellungen',
        name: 'Einstellungen',
        component: Einstellungen,
        meta: {
            name: 'Einstellungen',
            requiresAuth: true,
        },
        beforeEnter: adminGuard,
    },
];

Vue.use(VueRouter);

const router = new VueRouter({
    routes,
});

router.beforeEach((to, from, next) => {
    document.title = to.meta?.name ? to.meta.name + ' - IVSweb' : 'IVSweb';

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (store.getters['authentication/initialized'] && !store.getters['authentication/loggedIn']) {
            next({
                path: '/login',
                query: { redirect: to.fullPath },
            });
        } else {
            next();
        }
    } else {
        next(); // make sure to always call next()!
    }
});

export default router;
